import useVuelidate from '@vuelidate/core';
import { helpers, url as urlRule } from '@vuelidate/validators';
import { computed, reactive, ref, watch } from 'vue';

import useSalesforceProxyGate from '@/composables/useSalesforceProxyGate';
import type { DataSourceType } from '@/types/sources';

type ProxyFormPropertiesType = { base_url?: string };

const REQUIRED_MESSAGE = 'Proxy URL is required';
const UNPARSEABLE_MESSAGE =
  'Enter a valid URL, for example https://sf-proxy.acme.com';
const SCHEME_MESSAGE = 'The proxy URL must start with https://';
const CREDENTIALS_MESSAGE = 'Remove the username and password from the URL';
const PATH_MESSAGE = 'Enter the host only, without a path';
const QUERY_MESSAGE = 'Enter the host only, without a query string';
const FRAGMENT_MESSAGE = 'Enter the host only, without a fragment';
const PORT_MESSAGE = 'Enter the host only, without a port';
const TRAILING_DOT_MESSAGE = 'Enter the host without a trailing dot';
const IP_LITERAL_MESSAGE = 'Enter a host name, not an IP address';
const HOST_MESSAGE = 'Enter a public host name, for example sf-proxy.acme.com';

const ASCII_PRINTABLE = /^[\x21-\x7e]+$/;
const IP_LITERAL = /^\[|^[\d.]+$/;

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function shapeError(url: URL): string {
  if (url.protocol !== 'https:') return SCHEME_MESSAGE;
  if (url.username || url.password) return CREDENTIALS_MESSAGE;
  if (url.pathname !== '/') return PATH_MESSAGE;
  if (url.search) return QUERY_MESSAGE;
  if (url.hash) return FRAGMENT_MESSAGE;
  if (url.port) return PORT_MESSAGE;
  if (url.hostname.endsWith('.')) return TRAILING_DOT_MESSAGE;
  return '';
}

function hostError(url: URL): string {
  if (IP_LITERAL.test(url.hostname)) return IP_LITERAL_MESSAGE;
  if (urlRule.$validator(url.origin, null, null) !== true) return HOST_MESSAGE;
  return '';
}

function rewriteError(typed: string, url: URL): string {
  return typed.toLowerCase().startsWith(url.origin) ? '' : UNPARSEABLE_MESSAGE;
}

function parseProxyUrl(value: string): { url: URL | null; error: string } {
  const trimmed = value.trim();
  if (!trimmed) return { url: null, error: REQUIRED_MESSAGE };

  const url = ASCII_PRINTABLE.test(trimmed) ? parseUrl(trimmed) : null;
  if (!url) return { url: null, error: UNPARSEABLE_MESSAGE };

  const error = shapeError(url) || rewriteError(trimmed, url) || hostError(url);
  return { url: error ? null : url, error };
}

export default function useSalesforceProxyUrl() {
  const { isProxyUrlApplicable } = useSalesforceProxyGate();

  const fields = reactive({ proxyUrl: '' });
  const serverError = ref('');

  const rules = {
    proxyUrl: {
      shape: helpers.withMessage(
        ({ $model }: { $model: string }) => parseProxyUrl($model).error,
        (value: string) => !parseProxyUrl(value).error,
      ),
    },
  };

  const v$ = useVuelidate(rules, fields, { $lazy: true });

  watch(
    () => fields.proxyUrl,
    () => {
      serverError.value = '';
    },
    { flush: 'sync' },
  );

  const isValid = computed(() => Boolean(parseProxyUrl(fields.proxyUrl).url));

  const fieldError = computed(() => {
    if (serverError.value) return serverError.value;
    if (!v$.value.proxyUrl.$error) return '';
    return String(v$.value.proxyUrl.$errors[0]?.$message ?? '');
  });

  const fieldStatus = computed<'danger' | undefined>(() =>
    fieldError.value ? 'danger' : undefined,
  );

  function touchProxyUrl() {
    v$.value.proxyUrl.$touch();
  }

  function proxyFormPropertiesFor(
    integrationType: DataSourceType | null,
  ): ProxyFormPropertiesType {
    const { url } = parseProxyUrl(fields.proxyUrl);
    if (!isProxyUrlApplicable(integrationType) || !url) return {};
    return { base_url: url.origin };
  }

  return {
    fields,
    serverError,
    isValid,
    isProxyUrlApplicable,
    fieldError,
    fieldStatus,
    touchProxyUrl,
    proxyFormPropertiesFor,
  };
}
